/**
 * MergeVariableExtension — Tiptap custom Node for {{variable}} chips.
 *
 * TIPTAP_EXTENSIONS is the single source of truth for editor extensions used
 * by both the TemplateEditor (frontend) and any future server-side
 * generateHTML() calls (backend). Always import from this file.
 */

import { Node, mergeAttributes } from '@tiptap/core';
import type { Node as ProseMirrorNode, DOMOutputSpec } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';

// ---- MergeVariable Node ----

export const MergeVariableExtension = Node.create({
  name: 'mergeVariable',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      variable: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-variable'),
        renderHTML: (attrs: Record<string, unknown>) => ({
          'data-variable': attrs['variable'],
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-merge-variable]' }];
  },

  renderHTML({ HTMLAttributes, node }: { HTMLAttributes: Record<string, unknown>; node: ProseMirrorNode }): DOMOutputSpec {
    return [
      'span',
      mergeAttributes(HTMLAttributes as Record<string, string>, {
        'data-merge-variable': 'true',
        'data-variable': node.attrs['variable'] as string,
        class:
          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-tint text-brand-dark border border-brand-tint',
        contenteditable: 'false',
      }),
      `{{${String(node.attrs['variable'] ?? '')}}}`,
    ];
  },
});

// ---- Extension array — single source of truth ----

export const TIPTAP_EXTENSIONS = [
  StarterKit,
  Placeholder.configure({
    placeholder: 'Start typing your template...',
  }),
  CharacterCount,
  MergeVariableExtension,
];
